import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Star, Building, User, Calendar, ArrowRight, PlayCircle, StopCircle, MessageSquare } from "lucide-react";
import { JobEvaluationListItem } from "@/types/avaliacao";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { createLocalDate } from "@/utils/dateUtils";

interface AvaliacaoCardProps {
  avaliacao: JobEvaluationListItem;
  onClick?: () => void;
}

export function AvaliacaoCard({ avaliacao, onClick }: AvaliacaoCardProps) {
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendente';
      case 'email_sent':
        return 'Email Enviado';
      case 'rated':
        return 'Avaliado';
      case 'completed':
        return 'Completo';
      default:
        return status;
    }
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'pending':
        return 'outline';
      case 'email_sent':
        return 'secondary';
      case 'rated':
        return 'default';
      case 'completed':
        return 'default';
      default:
        return 'outline';
    }
  };

  const renderStars = (rating?: number) => {
    if (!rating) return null;
    
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
          <Star 
            key={star} 
            className={`h-3 w-3 ${
              star <= rating 
                ? 'text-yellow-400 fill-yellow-400' 
                : 'text-gray-300'
            }`} 
          />
        ))}
        <span className="ml-2 text-sm font-medium">{rating}/10</span>
      </div>
    );
  };

  // Cores diferentes para cada tipo de avaliador
  const getCardStyle = () => {
    if (avaliacao.evaluator_type === 'agency') {
      return 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20';
    } else {
      return 'border-l-4 border-l-green-500 bg-green-50/50 dark:bg-green-950/20';
    }
  };

  // Determinar quem avalia e quem é avaliado
  const getEvaluatorInfo = () => {
    if (avaliacao.evaluator_type === 'agency') {
      return {
        evaluator: {
          name: avaliacao.agencia_name || 'Agência não identificada',
          icon: Building,
          label: 'Agência'
        },
        evaluated: {
          name: avaliacao.freelancer_name || 'Freelancer não identificado',
          icon: User,
          label: 'Freelancer'
        }
      };
    } else {
      return {
        evaluator: {
          name: avaliacao.freelancer_name || 'Freelancer não identificado',
          icon: User,
          label: 'Freelancer'
        },
        evaluated: {
          name: avaliacao.agencia_name || 'Agência não identificada',
          icon: Building,
          label: 'Agência'
        }
      };
    }
  };

  const { evaluator, evaluated } = getEvaluatorInfo();

  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-all duration-200 ${
        onClick ? 'hover:bg-muted/50' : ''
      } ${getCardStyle()}`}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          {/* Seção Avaliador - Reduzida */}
          <div className="flex items-center gap-3 w-48">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <evaluator.icon className={`h-4 w-4 ${
                avaliacao.evaluator_type === 'agency' ? 'text-blue-600' : 'text-green-600'
              }`} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  {evaluator.label}
                </p>
                <p className="font-semibold text-sm truncate">{evaluator.name}</p>
              </div>
            </div>
          </div>

          {/* Seta */}
          <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mx-2" />

          {/* Seção Avaliado - Reduzida */}
          <div className="flex items-center gap-2 w-48">
            <evaluated.icon className={`h-4 w-4 ${
              avaliacao.evaluator_type === 'agency' ? 'text-green-600' : 'text-blue-600'
            }`} />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                {evaluated.label}
              </p>
              <p className="font-semibold text-sm truncate">{evaluated.name}</p>
            </div>
          </div>

          {/* Seção Avaliação e Status */}
          <div className="flex items-center gap-6 flex-1 ml-6">
            {/* Avaliação com estrelas - Máximo espaço flexível */}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1 text-center">
                Avaliação
              </p>
              <div className="flex flex-col items-center gap-2">
                {(avaliacao.media_geral || avaliacao.rating) ? (
                  renderStars(avaliacao.media_geral ?? avaliacao.rating)
                ) : (
                  <p className="text-sm text-muted-foreground">Não avaliado</p>
                )}
                
                {/* Prévia do comentário */}
                {avaliacao.feedback_text && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="w-full">
                        <div className="text-xs text-muted-foreground bg-gray-50 dark:bg-gray-800 rounded-md p-2 cursor-help hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                          <div className="flex items-start gap-1">
                            <MessageSquare className="h-3 w-3 text-blue-600 shrink-0 mt-0.5" />
                            <p className="text-left line-clamp-2 leading-tight">
                              {avaliacao.feedback_text.length > 300 
                                ? `${avaliacao.feedback_text.substring(0, 300)}...`
                                : avaliacao.feedback_text
                              }
                            </p>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[400px] p-3">
                        <div className="space-y-1">
                          <p className="font-medium text-sm">Comentário completo:</p>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {avaliacao.feedback_text}
                          </p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>

            {/* Status - Tamanho aumentado */}
            <div className="text-center w-32">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1">
                Status
              </p>
              <Badge variant={getStatusVariant(avaliacao.status)}>
                {getStatusLabel(avaliacao.status)}
              </Badge>
            </div>

            {/* Datas - Coluna menor */}
            <div className="text-center w-[150px]">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1">
                Datas
              </p>
              <div className="space-y-1">
                {/* Data de início do projeto */}
                {avaliacao.project_start_date && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <PlayCircle className="h-3 w-3 text-green-600" />
                    <span>
                      Início: {format(createLocalDate(avaliacao.project_start_date), 'dd/MM/yyyy', { locale: ptBR })}
                    </span>
                  </div>
                )}
                
                {/* Data de conclusão do projeto */}
                {avaliacao.project_end_date && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <StopCircle className="h-3 w-3 text-red-600" />
                    <span>
                      Fim: {format(createLocalDate(avaliacao.project_end_date), 'dd/MM/yyyy', { locale: ptBR })}
                    </span>
                  </div>
                )}
                
                {/* Data da avaliação ou criação */}
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span>
                    {avaliacao.rated_at ? (
                      `Avaliado ${formatDistanceToNow(new Date(avaliacao.rated_at), { 
                        addSuffix: true, 
                        locale: ptBR 
                      })}`
                    ) : avaliacao.created_at ? (
                      `Criado ${formatDistanceToNow(new Date(avaliacao.created_at), { 
                        addSuffix: true, 
                        locale: ptBR 
                      })}`
                    ) : (
                      'Data não disponível'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
